import { WittyToolCall } from '@agilelab/plugin-wb-witty-common';
import {
  Box,
  CircularProgress,
  Collapse,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useEffect, useId, useState } from 'react';
import { WbExpandToggle } from '../../WbExpandToggle';

const useStyles = makeStyles(
  theme => ({
    root: {
      margin: theme.spacing(0.5, 0),
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
      color: theme.palette.text.secondary,
    },
    toggleButton: {
      padding: 0,
      color: 'inherit',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.5, 0, 0, 1),
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    icon: {
      fontSize: '1rem',
    },
    status: {
      color: theme.palette.text.secondary,
      textTransform: 'capitalize',
    },
  }),
  { name: 'ToolActivity' },
);

export const ToolActivity = ({
  toolCalls,
  active = false,
}: {
  toolCalls: WittyToolCall[];
  active?: boolean;
}) => {
  const classes = useStyles();
  const isRunning = toolCalls.some(toolCall => toolCall.status === 'running');
  const [expanded, setExpanded] = useState(isRunning);
  const titleId = useId();
  const detailsId = useId();

  useEffect(() => {
    setExpanded(isRunning);
  }, [isRunning]);

  const renderStatusIcon = (toolCall: WittyToolCall) => {
    if (toolCall.status === 'running') {
      return <CircularProgress size={12} aria-hidden="true" />;
    }
    if (toolCall.status === 'failed') {
      return (
        <ErrorOutlineIcon
          color="error"
          className={classes.icon}
          aria-hidden="true"
        />
      );
    }
    return (
      <CheckCircleOutlineIcon
        color="primary"
        className={classes.icon}
        aria-hidden="true"
      />
    );
  };

  const activityLabel = active
    ? 'Tool activity'
    : `Used ${toolCalls.length} ${toolCalls.length === 1 ? 'tool' : 'tools'}`;

  return (
    <Box className={classes.root}>
      <Box className={classes.toggle}>
        <Typography variant="body2" id={titleId}>
          {activityLabel}
        </Typography>
        <WbExpandToggle
          className={classes.toggleButton}
          expanded={expanded}
          onClick={() => setExpanded(current => !current)}
          aria-labelledby={titleId}
          aria-controls={detailsId}
        />
      </Box>
      <Collapse in={expanded}>
        <Box className={classes.list} id={detailsId}>
          {toolCalls.map(toolCall => (
            <Box className={classes.item} key={toolCall.id}>
              {renderStatusIcon(toolCall)}
              <Typography variant="body2">{toolCall.name}</Typography>
              <Typography variant="caption" className={classes.status}>
                {toolCall.status}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
